// src/components/author/wizard/UploadManuscript.jsx
import React, { useState, useRef } from 'react';
import { Upload as UploadIcon, File as FileIcon, X, Download, Loader } from 'lucide-react';
import { showToast } from '../../../utils/toast';
import axios from '../../../utils/axios';
import {handleDownloadFile} from '../../../utils/utils.global';
// Each wizard file maps to the upload endpoint's `:field` param (also the multer
// field name) and to the `destination` folder the backend stores it in.
const UPLOAD_FIELDS = {
  manuscript: { field: 'manuscript_file', destination: 'manuscripts' },
  coverLetter: { field: 'cover_letter_file', destination: 'cover_letters' },
  tables: { field: 'tables', destination: 'tables' },
  figures: { field: 'figures', destination: 'figures' },
  supplementary: { field: 'supplementary_material', destination: 'supplementary' },
  trackedManuscript: { field: 'tracked_manuscript_file', destination: 'tracked_manuscripts' },
  graphicAbstract: { field: 'graphic_abstract', destination: 'graphic_abstracts' }
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const fileNameFromUrl = (url) => {
  try {
    return decodeURIComponent(String(url).split('/').pop()) || String(url);
  } catch (_) {
    return String(url);
  }
};

const UploadManuscript = ({ formData, updateFormData, onNext, onBack, saving, manuscriptId, ensureManuscriptId }) => {
  const [uploading, setUploading] = useState({});
  const fileInputs = {
    manuscript: useRef(),
    coverLetter: useRef(),
    tables: useRef(),
    figures: useRef(),
    supplementary: useRef(),
    trackedManuscript: useRef(),
    graphicAbstract: useRef()
  };

  const anyUploading = Object.values(uploading).some(Boolean);


  // Upload the file as soon as it is chosen, then keep only its URL in the form so
  // nothing binary is left to send when the submission is saved/submitted.
  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;

    const spec = UPLOAD_FIELDS[fieldName];
    if (!spec) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png'
    ];

    if (!allowedTypes.includes(file.type) &&
      !file.name.match(/\.(pdf|doc|docx|xls|xlsx|jpg|jpeg|png)$/i)) {
      showToast('Please upload a valid file format (PDF, Word, Excel, or Images)', 'error');
      if (fileInputs[fieldName].current) fileInputs[fieldName].current.value = '';
      return;
    }

    // Validate file size (50MB)
    if (file.size > MAX_FILE_SIZE) {
      showToast('File size exceeds 50MB', 'error');
      if (fileInputs[fieldName].current) fileInputs[fieldName].current.value = '';
      return;
    }

    setUploading(prev => ({ ...prev, [fieldName]: true }));

    try {
      // The upload is attached to a submission row, so the draft has to exist first.
      let targetId = manuscriptId;
      if (!targetId && typeof ensureManuscriptId === 'function') {
        targetId = await ensureManuscriptId();
      }
      if (!targetId) {
        throw new Error('Could not start your submission. Please save your progress and try again.');
      }

      const body = new FormData();
      // `destination` has to precede the file part: multer resolves the storage
      // folder from req.body, which is only populated for fields parsed earlier.
      body.append('destination', spec.destination);
      body.append('manuscriptId', targetId);
      body.append(spec.field, file);

      const response = await axios.post(
        `/api/authors/submission/uploadSingleFile/${spec.field}`,
        body,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-destination': spec.destination
          }
        }
      );

      const fileUrl = response.data?.fileUrl;
      if (!fileUrl) {
        throw new Error(response.data?.error || 'Upload failed');
      }

      // Attach the URL to the form instead of the File object
      updateFormData(prev => ({ files: { ...prev.files, [fieldName]: fileUrl } }));
      showToast(`${file.name} uploaded successfully`, 'success');
    } catch (error) {
      console.error(`Error uploading ${fieldName}:`, error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Upload failed. Please try again.';
      showToast(message, 'error');

      // Allow re-selecting the same file after a failure
      if (fileInputs[fieldName].current) fileInputs[fieldName].current.value = '';
    } finally {
      setUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const removeFile = (fieldName) => {
    updateFormData(prev => ({ files: { ...prev.files, [fieldName]: null } }));

    // Reset file input
    if (fileInputs[fieldName].current) {
      fileInputs[fieldName].current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return ((bytes / (1024 * 1024)).toFixed(2) || 5) + ' MB';
  };

  const renderFileControl = (fieldName, label, required = false) => {
    const file = formData.files[fieldName];
    const isUploading = uploading[fieldName];
    const isFileObject = file instanceof File;
    const displayName = isFileObject ? file.name : fileNameFromUrl(file);

    return (
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {file ? (
          <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center flex-1 min-w-0">
              <FileIcon className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
              <div className="min-w-0 max-w-80">
                <p className="text-sm font-medium text-gray-900 break-words truncate">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500">
                  {isFileObject ? formatFileSize(file.size) : 'Uploaded'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0">
              {!isFileObject && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  Ready
                </span>
              )}
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-purple-600 hover:bg-white rounded-lg transition-colors"
                onClick={() => handleDownloadFile(file, fieldName)}
                title={`Download ${displayName}`}
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => removeFile(fieldName)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => !isUploading && fileInputs[fieldName].current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isUploading
              ? 'border-purple-300 bg-purple-50'
              : 'border-gray-300 hover:border-purple-500 cursor-pointer'
              }`}
          >
            <input
              ref={fileInputs[fieldName]}
              type="file"
              accept=".pdf,.doc,.docx,"
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleFileUpload(fieldName, e.target.files[0]);
                }
              }}
              className="hidden"
              disabled={isUploading}
            />
            {isUploading ? (
              <div className="flex items-center justify-center">
                <Loader className="w-5 h-5 animate-spin text-purple-600 mr-2" />
                <span className="text-purple-600">Uploading...</span>
              </div>
            ) : (
              <>
                <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, Images (Max 50MB)</p>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const allRequiredUploaded = () => {
    return formData.files.manuscript && formData.files.coverLetter;
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6">Upload Manuscript Files</h3>

      <div className="space-y-4">
        {renderFileControl('manuscript', 'Main Manuscript File', true)}
        {renderFileControl('coverLetter', 'Cover Letter', true)}
        {renderFileControl('tables', 'Tables')}
        {renderFileControl('figures', 'Figures')}
        {renderFileControl('supplementary', 'Supplementary Material')}
        {renderFileControl('trackedManuscript', 'Main Manuscript File with Tracked Changes')}
        {renderFileControl('graphicAbstract', 'Point-by-Point Response to Reviewers')}
      </div>

      <p className="text-sm text-red-500 mt-6 mb-6">* Indicates Mandatory Fields</p>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Back
        </button>

        <button
          type="button"
          onClick={onNext}
          disabled={saving || anyUploading || !allRequiredUploaded()}
          className={`px-8 py-3 rounded-lg transition-colors font-medium ${!allRequiredUploaded() || saving || anyUploading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
        >
          {saving || anyUploading ? (
            <>
              <Loader className="w-4 h-4 animate-spin inline mr-2" />
              {anyUploading ? 'Uploading files...' : 'Saving...'}
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default UploadManuscript;
